// src/components/analytics/AssetPerformanceDashboard.js
// --- UPDATE (round 3: live + real) ----------------------------------------------------------------
// • Refresh reloaded the whole browser page (window.location.reload) — it now remounts the widgets,
//   so they refetch while the rest of the Analytics Hub (and its websocket) stays as it is.
// • CSV export read problemAssetsRes.data.data, which is undefined for this API, so it always said
//   "No data"; the response shape is now handled whichever way it arrives.
// • "Updated Xm ago" never moved — it ticks every 30 s.
// • LifecycleCostWidget, ReliabilityMetricsWidget and AssetDowntimeWidget now receive the date
//   range (only ProblemAssetsWidget did).
// • Live: asset predictions, analysis runs and health-score recalculations from the Analytics Hub
//   (liveVersion / lastLiveEvent) refresh the widgets — at most once a minute.
// • AI strip: which models feed this tab, with Explain buttons and Ask FlowSense AI.
// • Debug console logging on mount/unmount removed.
// --- END UPDATE ------------------------------------------------------------------------------------
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, SimpleGrid, Heading, VStack, HStack, Button, Icon, IconButton, Tooltip, Badge, Text,
  useColorModeValue, Flex, useToast, Progress,
} from '@chakra-ui/react';
import {
  MdPrint, MdDownload, MdRefresh, MdDashboard, MdInfo, MdCheckCircle, MdWarning, MdAutoAwesome, MdBolt,
} from 'react-icons/md';
import analyticsService from '../../services/analyticsService';
import { useReactToPrint } from 'react-to-print';
import Papa from 'papaparse';
import ReliabilityMetricsWidget from './ReliabilityMetricsWidget';
import ProblemAssetsWidget from './ProblemAssetsWidget';
import AssetDowntimeWidget from './AssetDowntimeWidget';
import LifecycleCostWidget from './LifecycleCostWidget';
import { ExplainButton } from '../ml/FlowSenseAIExplainer';

const LIVE_TYPES = new Set(['asset_prediction_update', 'predictive_analysis_complete', 'predictive_risk_update', 'asset_health_scores_updated', 'rul_batch_complete']);
const LIVE_MIN_GAP_MS = 60000;

const rowsOf = (res) => {
  for (const c of [res?.data?.data, res?.data, res]) if (Array.isArray(c)) return c;
  return [];
};
const agoText = (d, now) => {
  if (!d) return '—';
  const s = Math.max(Math.round((now - d.getTime()) / 1000), 0);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
};

function AssetPerformanceDashboard({ dateRange, liveVersion = 0, lastLiveEvent = null, onAskAI = null }) {
  const [lastRefresh, setLastRefresh] = useState(() => new Date());
  const [now, setNow] = useState(() => Date.now());
  const [refreshKey, setRefreshKey] = useState(0);
  const [liveUpdates, setLiveUpdates] = useState(0);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const componentRef = useRef();
  const mounted = useRef(true);
  const toast = useToast();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const aiBg = useColorModeValue('purple.50', 'whiteAlpha.100');

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  useEffect(() => { setLastRefresh(new Date()); }, [dateRange?.start, dateRange?.end]);

  const refreshWidgets = useCallback(() => {
    setRefreshKey(k => k + 1);
    setLastRefresh(new Date());
    setNow(Date.now());
  }, []);

  // live refresh (throttled)
  const lastLive = useRef(liveVersion);
  const lastBump = useRef(0);
  const liveTimer = useRef(null);
  useEffect(() => {
    if (liveVersion === lastLive.current) return;
    lastLive.current = liveVersion;
    if (!lastLiveEvent || !LIVE_TYPES.has(lastLiveEvent.type) || liveTimer.current) return;
    const wait = Math.max(LIVE_MIN_GAP_MS - (Date.now() - lastBump.current), 0);
    liveTimer.current = setTimeout(() => {
      liveTimer.current = null;
      if (!mounted.current) return;
      lastBump.current = Date.now();
      setLiveUpdates(n => n + 1);
      refreshWidgets();
    }, wait);
  }, [liveVersion, lastLiveEvent, refreshWidgets]);
  useEffect(() => () => { if (liveTimer.current) clearTimeout(liveTimer.current); }, []);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'Asset_Performance_Report',
    onAfterPrint: () => toast({ title: 'Report sent to printer', status: 'success', duration: 2500 }),
  });

  const handleExportCSV = useCallback(async () => {
    setIsExporting(true);
    setExportProgress(20);
    try {
      const res = await analyticsService.getWidgetData('problem-assets', { startDate: dateRange?.start, endDate: dateRange?.end });
      setExportProgress(60);
      const problemAssets = rowsOf(res);
      if (problemAssets.length === 0) {
        toast({ title: 'No data available', description: 'No problem assets in this period.', status: 'warning', duration: 4000 });
        return;
      }
      const csv = Papa.unparse(problemAssets);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      const filename = `problem_assets_${dateRange?.start || ''}_${dateRange?.end || ''}.csv`;
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportProgress(100);
      toast({ title: 'Export successful', description: `${problemAssets.length} records exported to ${filename}`, status: 'success', duration: 4000 });
    } catch (exportError) {
      toast({ title: 'Export failed', description: exportError.message || 'Could not export data. Please try again.', status: 'error', duration: 6000 });
    } finally {
      if (mounted.current) { setIsExporting(false); setExportProgress(0); }
    }
  }, [dateRange, toast]);

  const summaryCards = [
    { icon: MdCheckCircle, color: 'blue.500', title: 'RELIABILITY METRICS', text: 'MTTR, MTBF, FTFR analysis' },
    { icon: MdWarning, color: 'red.500', title: 'PROBLEM ASSETS', text: 'Top 10 by cost impact in the period' },
    { icon: MdWarning, color: 'orange.500', title: 'DOWNTIME TRACKING', text: 'Operational impact by asset' },
    { icon: MdInfo, color: 'green.500', title: 'LIFECYCLE COSTS', text: 'Total cost of ownership' },
  ];

  return (
    <Box bg={bgColor} p={{ base: 2, md: 6 }} borderRadius="lg">
      <VStack ref={componentRef} spacing={8} align="stretch">
        <Box p={6} bg={cardBg} borderRadius="lg" boxShadow="md" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <HStack spacing={3}>
              <Icon as={MdDashboard} boxSize={8} color="blue.500" />
              <VStack align="flex-start" spacing={0}>
                <Heading as="h2" size="lg">Asset Performance Analytics</Heading>
                <HStack spacing={2} mt={1} flexWrap="wrap">
                  <Badge colorScheme="blue" fontSize="xs">{dateRange?.start} to {dateRange?.end}</Badge>
                  <Tooltip label={`Last refreshed: ${lastRefresh.toLocaleString()}`} hasArrow>
                    <Badge colorScheme="green" fontSize="xs" cursor="help" data-testid="updated-ago">Updated {agoText(lastRefresh, now)}</Badge>
                  </Tooltip>
                  {liveUpdates > 0 && (
                    <Tooltip label="Refreshed automatically after live asset predictions" hasArrow>
                      <Badge colorScheme="purple" fontSize="xs"><Icon as={MdBolt} mr={1} />{liveUpdates} live update{liveUpdates === 1 ? '' : 's'}</Badge>
                    </Tooltip>
                  )}
                </HStack>
              </VStack>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <Tooltip label="Refresh all widgets" hasArrow>
                <IconButton icon={<MdRefresh />} variant="outline" colorScheme="blue" onClick={refreshWidgets} aria-label="Refresh dashboard" />
              </Tooltip>
              <Button leftIcon={<MdDownload />} variant="outline" colorScheme="green" onClick={handleExportCSV} isLoading={isExporting} loadingText="Exporting...">Export CSV</Button>
              <Button leftIcon={<MdPrint />} variant="outline" colorScheme="purple" onClick={handlePrint}>Print PDF</Button>
            </HStack>
          </Flex>
          {isExporting && exportProgress > 0 && (
            <Box mt={4}>
              <Progress value={exportProgress} size="sm" colorScheme="green" borderRadius="full" hasStripe isAnimated />
            </Box>
          )}
          <Flex mt={4} p={3} bg={aiBg} borderRadius="md" align="center" gap={2} wrap="wrap" data-testid="asset-ai-strip">
            <Icon as={MdAutoAwesome} color="purple.400" />
            <Text fontSize="sm" color={textColor}>AI on this tab:</Text>
            <ExplainButton topic="RUL_PREDICTION" label="Remaining useful life" />
            <ExplainButton topic="PRESCRIPTIVE" label="Repair vs replace" />
            {onAskAI && (
              <Button size="xs" colorScheme="purple" ml="auto" onClick={() => onAskAI(`Between ${dateRange?.start} and ${dateRange?.end}, which assets hurt reliability and cost the most, and what should we do about them?`)}>
                Ask about these assets
              </Button>
            )}
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          {summaryCards.map(c => (
            <Box key={c.title} p={4} bg={cardBg} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor} borderLeftWidth="4px" borderLeftColor={c.color}>
              <HStack spacing={3}>
                <Icon as={c.icon} boxSize={6} color={c.color} />
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">{c.title}</Text>
                  <Text fontSize="sm" color={textColor}>{c.text}</Text>
                </VStack>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>

        <Box><ReliabilityMetricsWidget dateRange={dateRange} key={`rel-${refreshKey}`} /></Box>
        <Box><ProblemAssetsWidget dateRange={dateRange} key={`prob-${refreshKey}`} /></Box>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          <LifecycleCostWidget dateRange={dateRange} key={`lcc-${refreshKey}`} />
          <AssetDowntimeWidget dateRange={dateRange} key={`down-${refreshKey}`} />
        </SimpleGrid>

        <Box p={4} bg={cardBg} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between" align="center" fontSize="sm" color="gray.500" wrap="wrap" gap={2}>
            <HStack>
              <Icon as={MdInfo} />
              <Text>Widgets refresh after live asset predictions (at most once a minute). Last refresh {lastRefresh.toLocaleTimeString()}.</Text>
            </HStack>
          </Flex>
        </Box>
      </VStack>
    </Box>
  );
}

export default AssetPerformanceDashboard;
